/**
 * TutorialContext — estado del tutorial guiado.
 *
 * Guarda qué guion se está siguiendo, por qué paso va, en qué modo (simular,
 * crear, crear y poder deshacer) y qué ha ido creando el usuario, para poder
 * reanudar donde se dejó y deshacer al final.
 *
 * El progreso se persiste en localStorage a propósito: un tutorial de doce
 * pasos con creación de registros se corta (recarga, un aviso, una llamada) y
 * perder el hilo sería muy frustrante.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { getTutorial } from './tutorials';

const PROGRESS_KEY = 'cmdb-tour-progress';
const CREATED_KEY = 'cmdb-tour-created';

export const TOUR_MODES = [
  {
    id: 'auto',
    icon: '🎬',
    label: 'Que lo haga él solo (demo)',
    hint: 'El tutorial va haciendo los clics por ti y te dice qué está haciendo, en un panel abajo que no tapa la pantalla. Puedes pausarlo, acelerarlo o pasar a hacerlo tú en cualquier momento. Al terminar te deja deshacer lo creado.',
  },
  {
    id: 'simulate',
    icon: '🧪',
    label: 'Solo simular',
    hint: 'Te lo enseño sin tocar la base de datos. Los pasos que guardan algo se marcan como vistos.',
  },
  {
    id: 'create',
    icon: '✅',
    label: 'Crear de verdad',
    hint: 'Vas creando el servicio de verdad en la empresa seleccionada. Se queda en el inventario.',
  },
  {
    id: 'create-undo',
    icon: '↩️',
    label: 'Crear y poder deshacer',
    hint: 'Igual que el anterior, pero al final tendrás un botón para borrar todo lo que haya creado el tutorial.',
  },
];

function readJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* localStorage lleno o bloqueado: el tutorial funciona igual, sin memoria */
  }
}

const TutorialContext = createContext(null);

export function TutorialProvider({ children, companyId }) {
  const [state, setState] = useState(() => readJSON(PROGRESS_KEY, null));
  const [created, setCreated] = useState(() => readJSON(CREATED_KEY, []));
  const [undoing, setUndoing] = useState(false);
  const [undoResult, setUndoResult] = useState(null);

  const tour = state?.tourId ? getTutorial(state.tourId) : null;
  const mode = state?.mode || 'simulate';
  const stepIndex = state?.stepIndex ?? 0;
  const step = tour?.steps?.[stepIndex] || null;
  const total = tour?.steps?.length || 0;
  const isSimulating = mode === 'simulate';

  useEffect(() => { writeJSON(PROGRESS_KEY, state); }, [state]);
  useEffect(() => { writeJSON(CREATED_KEY, created); }, [created]);

  const start = useCallback((tourId, selectedMode = 'simulate') => {
    const found = getTutorial(tourId);
    if (!found) return;
    setUndoResult(null);
    setState({
      tourId,
      mode: selectedMode,
      stepIndex: 0,
      playing: selectedMode === 'auto',   // la demo arranca reproduciéndose
      speed: 1,
      companyId: companyId ?? null,
      serviceId: null,
      startedAt: new Date().toISOString(),
    });
  }, [companyId]);

  const stop = useCallback(() => setState(null), []);

  /** Pausa: el tutorial deja de pintarse pero se recuerda por dónde iba. */
  const pause = useCallback(() => {
    setState(prev => (prev ? { ...prev, paused: true } : prev));
  }, []);

  /** Reanuda un tutorial pausado (o lo sigue si estaba minimizado). */
  const resume = useCallback(() => {
    setState(prev => (prev ? { ...prev, paused: false } : prev));
  }, []);

  /** Se olvida el progreso guardado: empezar de cero. */
  const forget = useCallback(() => setState(null), []);

  /** Reproducción de la demo: pausar/reanudar y velocidad. */
  const setPlaying = useCallback((value) => {
    setState(prev => (prev ? { ...prev, playing: Boolean(value) } : prev));
  }, []);

  const setSpeed = useCallback((value) => {
    setState(prev => (prev ? { ...prev, speed: value } : prev));
  }, []);

  /**
   * Deja la demo y pasa a "lo hago yo": conserva el paso en el que va y el
   * registro de lo creado, así que el deshacer del final sigue funcionando.
   */
  const switchToManual = useCallback(() => {
    setState(prev => (prev ? { ...prev, mode: 'create-undo', playing: false } : prev));
  }, []);

  const goTo = useCallback((index) => {
    setState(prev => {
      if (!prev) return prev;
      const found = getTutorial(prev.tourId);
      const max = (found?.steps?.length || 1) - 1;
      return { ...prev, stepIndex: Math.max(0, Math.min(max, index)) };
    });
  }, []);

  const next = useCallback(() => {
    setState(prev => {
      if (!prev) return prev;
      const found = getTutorial(prev.tourId);
      const max = (found?.steps?.length || 1) - 1;
      return { ...prev, stepIndex: Math.min(max, prev.stepIndex + 1) };
    });
  }, []);

  const prev = useCallback(() => goTo(stepIndex - 1), [goTo, stepIndex]);

  /** Recuerda el servicio que se acaba de crear (para poder deshacerlo). */
  const trackService = useCallback((serviceId, name) => {
    if (!serviceId) return;
    setState(s => (s ? { ...s, serviceId } : s));
    setCreated(list => {
      if (list.some(it => it.type === 'service' && it.id === serviceId)) return list;
      return [...list, { type: 'service', id: serviceId, name, at: new Date().toISOString() }];
    });
  }, []);

  /**
   * Fija el servicio sobre el que trabaja el guion cuando YA existe (guiones que
   * ordenan el mapa de algo del inventario en vez de crear un servicio nuevo).
   * No se apunta como «creado»: no hay que borrarlo al deshacer.
   */
  const resolveServiceByName = useCallback(async (name) => {
    if (!companyId || !name) return null;
    try {
      const list = await api.getServices(companyId);
      const arr = Array.isArray(list) ? list : (list.services || []);
      const needle = name.trim().toLowerCase();
      const hit = arr.find(s => (s.display_name || s.name || '').toLowerCase().includes(needle));
      if (!hit) return null;
      setState(s => (s ? { ...s, serviceId: hit.id } : s));
      return hit.id;
    } catch {
      return null;
    }
  }, [companyId]);

  /**
   * Recuerda una relación creada por el tutorial. Hace falta para los guiones
   * que trabajan sobre un servicio que YA existe (no se puede "deshacer"
   * borrando el servicio): ahí lo que hay que deshacer es la relación.
   */
  const trackRelation = useCallback((relationId, label) => {
    if (!relationId) return;
    setCreated(list => {
      if (list.some(it => it.type === 'relation' && it.id === relationId)) return list;
      return [...list, { type: 'relation', id: relationId, name: label || `relación #${relationId}`, at: new Date().toISOString() }];
    });
  }, []);

  /**
   * Borra lo que ha creado el tutorial: primero las relaciones del modelo en
   * las que participa el servicio (la tabla del Diseñador no borra en cascada,
   * porque es polimórfica) y después el propio servicio.
   */
  const undoAll = useCallback(async () => {
    if (!companyId) return { ok: false, message: 'Selecciona una empresa antes de deshacer.' };
    setUndoing(true);
    setUndoResult(null);
    const removed = [];
    try {
      // Primero las relaciones sueltas (guiones que trabajan sobre un servicio
      // ya existente), y después los servicios creados de cero.
      for (const item of created) {
        if (item.type !== 'relation') continue;
        try {
          await api.sdDeleteRelation(item.id, companyId);
          removed.push(item.name);
        } catch { /* ya no estaba */ }
      }
      for (const item of created) {
        if (item.type !== 'service') continue;
        try {
          const deps = await api.getServiceDependencies(item.id, companyId).catch(() => null);
          const relIds = [
            ...(deps?.relations_out || []).map(r => r.id),
            ...(deps?.relations_in || []).map(r => r.id),
          ];
          for (const relId of relIds) {
            await api.sdDeleteRelation(relId, companyId).catch(() => {});
          }
        } catch {
          /* sin relaciones o sin permisos: se intenta borrar el servicio igual */
        }
        await api.deleteService(item.id, companyId);
        removed.push(item.name || `#${item.id}`);
      }
      setCreated([]);
      const result = {
        ok: true,
        message: removed.length
          ? `Deshecho: ${removed.length} cosa(s) del tutorial (${removed.join(', ')}).`
          : 'No había nada que deshacer.',
      };
      setUndoResult(result);
      return result;
    } catch (e) {
      const result = {
        ok: false,
        message: `No se pudo deshacer del todo: ${e.message}. Revisa el catálogo de servicios.`,
      };
      setUndoResult(result);
      return result;
    } finally {
      setUndoing(false);
    }
  }, [created, companyId]);

  /** ¿Hay algo pendiente de borrar de una sesión anterior? */
  const hasCreated = created.length > 0;

  const value = useMemo(() => ({
    active: Boolean(tour && step) && !state?.paused,
    paused: Boolean(state?.paused),
    tour,
    step,
    stepIndex,
    total,
    mode,
    isSimulating,
    isAuto: mode === 'auto',
    playing: state?.playing !== false,
    speed: state?.speed || 1,
    serviceId: state?.serviceId || null,
    companyId: companyId ?? null,
    created,
    hasCreated,
    undoing,
    undoResult,
    start,
    stop,
    pause,
    resume,
    forget,
    setPlaying,
    setSpeed,
    switchToManual,
    next,
    prev,
    goTo,
    trackService,
    trackRelation,
    resolveServiceByName,
    undoAll,
  }), [
    tour, step, stepIndex, total, mode, isSimulating, state?.serviceId, state?.paused, state?.playing, state?.speed, companyId, created,
    hasCreated, undoing, undoResult, start, stop, pause, resume, forget, setPlaying, setSpeed, switchToManual,
    next, prev, goTo, trackService, trackRelation, resolveServiceByName, undoAll,
  ]);

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial debe usarse dentro de TutorialProvider');
  return ctx;
}
